import { type FC, useMemo } from 'react'
import { ImageCard } from '@/components/commons/image-card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { CurationPhotosetSpaceCounter } from '@/features/photoset/components/curation-photoset-space-counter'
import { CurationPhotosetSpaceImages } from '@/features/photoset/components/curation-photoset-space-images'
import type {
	Image,
	PhotosetSpace,
	PhotosetSpacesChecks,
	Space
} from '@/features/photoset/types'
import { spaceNameToLabel } from '@/features/photoset/utils'

interface CurationPhotosetSpaceProps {
	asSkeleton?: undefined
	spaceId: number
	spaceName: Space['key_name']
	images: Omit<Image, 'space'>[]
	spaceIndex: number
	spaceChecks: PhotosetSpacesChecks
	curationTouched: boolean
	hideAllUnselected: boolean
	currentDetailImageIndex: number | null
	spaceFlagValue: PhotosetSpace['flag_key_name']
	cbSetHideAllUnselected: () => void
	onExpandImage: (spaceIndex: number, imageIndex: number) => void
}

interface CurationPhotosetSpaceSkeletonProps {
	asSkeleton: true
	spaceId?: number
	spaceFlagValue?: undefined
	spaceName?: undefined
	images?: undefined
	spaceIndex?: undefined
	spaceChecks?: undefined
	curationTouched?: undefined
	cbSetHideAllUnselected?: undefined
	hideAllUnselected?: undefined
	onExpandImage?: undefined
}

export const CurationPhotosetSpace: FC<
	CurationPhotosetSpaceProps | CurationPhotosetSpaceSkeletonProps
> = ({
	asSkeleton,
	spaceId,
	curationTouched,
	hideAllUnselected,
	images,
	spaceName,
	spaceIndex,
	spaceChecks,
	cbSetHideAllUnselected,
	onExpandImage,
	spaceFlagValue
}) => {
	const visibleImages = useMemo((): Omit<Image, 'space'>[] => {
		if (images) {
			if (hideAllUnselected) {
				return images?.filter((image) => image.is_final_select)
			}
			return images
		}
		return []
	}, [images, hideAllUnselected])

	if (asSkeleton) {
		return (
			<div className='flex flex-col'>
				<div className='relative z-[1] flex w-full flex-col gap-1 px-6 py-4'>
					<Skeleton height={28} width={100} />
					<Skeleton height={20} width={200} />
				</div>
				<Separator className='mb-4' orientation='horizontal' />
				<div className='flex gap-4'>
					<ImageCard asSkeleton />
					<ImageCard asSkeleton />
					<ImageCard asSkeleton />
					<ImageCard asSkeleton />
				</div>
			</div>
		)
	}

	return (
		<div className='flex flex-col'>
			<div className='relative z-[1] flex w-full flex-col gap-1 px-6 py-4 [&_h4]:font-medium [&_h4]:text-lg [&_h4]:text-neutral-900 [&_p]:text-neutral-600 [&_p]:text-sm'>
				<h4>{spaceNameToLabel[spaceName]}</h4>
				<CurationPhotosetSpaceCounter
					curationTouched={curationTouched}
					photosetSpaceId={spaceId}
					spaceChecks={spaceChecks}
					spaceFlagValue={spaceFlagValue}
					spaceIndex={spaceIndex}
				/>
			</div>

			<Separator orientation='horizontal' />

			<CurationPhotosetSpaceImages
				cbSetHideAllUnselected={cbSetHideAllUnselected}
				images={visibleImages}
				onExpandImage={onExpandImage}
				spaceId={spaceId}
				spaceIndex={spaceIndex}
			/>
		</div>
	)
}

CurationPhotosetSpace.displayName = 'CurationPhotosetSpace'
